import { Button, Checkbox, Table, Tag } from '@ph-react-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMatch, useSearchParams } from 'react-router-dom';
import { useExportSchoolTaxesReport } from '../../../../hooks/taxes/school-taxes/admin/useExportSchoolTaxesReport';
import {
  createQueryKey,
  usePaginatedPromisedSchoolTaxes,
} from '../../../../hooks/taxes/school-taxes/admin/usePaginatedPromisedSchoolTaxes';
import { useModal } from '../../../../hooks/useModal';
import { useSelectAll } from '../../../../hooks/useSelectAll';
import type { TaxUser } from '../../../../models/taxes/tax-user';
import {
  Scholarship,
  type ScholarshipType,
} from '../../../../models/users/students/scholarship-type';
import {
  CONFIRM_BANK_PAYMENT,
  CONFIRM_DELETE_SCHOOL_TAX,
} from '../../../../utils/constants/users/modals';
import { displayPrice } from '../../../../utils/displayPrice';
import { TaxesOrdering } from '../../../../utils/enums/taxes-ordering.enum';
import { ActionMenuHorizontal } from '../../../shared/components/ActionMenuHorizontal';
import { EmptyState } from '../../../shared/components/EmptyState';
import ExportButton from '../../../shared/components/ExportButton';
import { Pagination } from '../../../shared/components/Pagination';
import { TableSkeleton } from '../../../shared/components/skeletons/TableSkeleton';
import { ThSortItem } from '../../../shared/components/tables/ThSortItem';
import { TaxesFilters } from '../../../shared/components/TaxesFilters';
import { isPenaltyApplied } from '../parent/utils/schoolTaxesUtils';

export function AdminSchoolTaxesReview() {
  const match = useMatch('/dashboard/taxes/school-taxes');
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { open: openConfirmBankPaymentModal } = useModal(CONFIRM_BANK_PAYMENT);
  const { open: openConfirmDeleteModal } = useModal(CONFIRM_DELETE_SCHOOL_TAX);
  const exportReport = useExportSchoolTaxesReport(searchParams);

  const {
    data: adminSchoolTaxes,
    isSuccess,
    isPending,
    isError,
  } = usePaginatedPromisedSchoolTaxes(searchParams);

  const { control, handleSelectAll, selectAll, dirtyFields, watch } =
    useSelectAll(adminSchoolTaxes?.result, 'isPaid');

  const users = watch('items');

  useEffect(() => {
    if (adminSchoolTaxes) {
      queryClient.setQueryData(
        ['promised-school-taxes', createQueryKey(searchParams)],
        adminSchoolTaxes
      );
    }
  }, [adminSchoolTaxes, searchParams, queryClient]);

  const renderHeader = (users: TaxUser[], dirtyFields: any) => {
    const onOpenMany = (isMarkAsPaid: boolean) => {
      const ids = users
        .filter((user) => user.value)
        .map((user) => user.id)
        .join('&');
      isMarkAsPaid
        ? openConfirmBankPaymentModal({
            userIds: ids,
          })
        : openConfirmDeleteModal({ userIds: ids });
    };
    return (
      <>
        <Button
          disabled={!dirtyFields.items?.some((user: TaxUser) => user.value)}
          className="taxes__transportation__filter__bulk"
          onClick={() => onOpenMany(true)}
        >
          {t('ACTIONS.MARK_AS_PAID')}
        </Button>
        <Button
          disabled={!dirtyFields.items?.some((user: TaxUser) => user.value)}
          className="taxes__transportation__filter__bulk"
          onClick={() => onOpenMany(false)}
        >
          {t('ACTIONS.DELETE')}
        </Button>
      </>
    );
  };

  const onMarkAsPaid = useCallback(
    (ids: string[]) => {
      const selectedIds = ids.join('&');
      openConfirmBankPaymentModal({
        userIds: selectedIds,
      });
    },
    [openConfirmBankPaymentModal]
  );

  const onDelete = useCallback(
    (ids: string[]) => {
      const selectedIds = ids.join('&');
      openConfirmDeleteModal({
        userIds: selectedIds,
      });
    },
    [openConfirmDeleteModal]
  );

  const headers = [
    <div className="users__table__select-all">
      <Checkbox
        onChange={(checked) => handleSelectAll(checked)}
        value={selectAll}
      />
      {t('PAYMENT_SCHEMES.TABLE.HEADERS.INSTALLMENTS')}
    </div>,

    t('TAXES.TABLE.HEADERS.PAYMENT_ID'),
    t('TAXES.TABLE.HEADERS.ORDER_ID'),
    <ThSortItem
      title={t('USERS.TABLE.HEADERS.NAME')}
      ordering={TaxesOrdering.Name}
    />,
    t('TAXES.TABLE.HEADERS.TYPE'),
    t('TAXES.TABLE.HEADERS.GENERATED_ON'),
    t('TAXES.TABLE.HEADERS.SCHOOL_YEAR'),
    t('TAXES.TABLE.HEADERS.PERCENT'),
    t('TAXES.TABLE.HEADERS.PAID_BY'),
    <ThSortItem
      title={t('TAXES.TABLE.HEADERS.PAID_AT')}
      ordering={TaxesOrdering.PaidAt}
    ></ThSortItem>,
    t('TAXES.TABLE.HEADERS.AMOUNT'),
    t('TAXES.TABLE.HEADERS.DUE_DATE'),
    t('TAXES.TABLE.HEADERS.PENALTY'),
    t('TAXES.TABLE.HEADERS.TOTAL_AMOUNT'),
    t('TAXES.TABLE.HEADERS.STATUS'),
    t('TAXES.TABLE.HEADERS.PAID_WITH'),
    t('USERS.TABLE.HEADERS.ACTION'),
  ];

  const isOnSchoolTaxesScholarship = (scholarshipType: ScholarshipType) =>
    scholarshipType !== Scholarship.NO_SCHOLARSHIP;

  const tableChildren = useMemo(() => {
    return adminSchoolTaxes?.result.reduce((acc: any, user, index) => {
      const penaltyApplied = isPenaltyApplied(user);
      const totalAmount = user.amount + (penaltyApplied ? user.penalty : 0);
      const isOverdue =
        !user.isPaid && user.dueDate && new Date(user.dueDate) < new Date();

      let userArray = [
        <div className="users__table__checkbox">
          <Controller
            name={`items.${index}.value`}
            control={control}
            disabled={user.isPaid}
            render={({ field }) => (
              <Checkbox
                className="users__table-first"
                {...field}
                value={user.isPaid ? false : field.value}
              />
            )}
          />
          {t('PAYMENT_SCHEMES.DRAWER.LABELS.INSTALLMENT')} #{user.index + 1}
        </div>,
        <span>{user.code}</span>,
        user.order || '',
        user.name,
        isOnSchoolTaxesScholarship(user.scholarshipType)
          ? t('TAXES.LABELS.SCHOLARSHIP')
          : t('TAXES.LABELS.REGULAR'),
        user.generatedAt
          ? format(new Date(user.generatedAt), 'dd.MM.yyyy')
          : '',
        <span>
          {user.startYear} - {user.endYear}
        </span>,
        `${user.percentage}%`,
        user.isPaid ? user.paidByName : '',
        user.isPaid && user.paidAt
          ? format(new Date(user.paidAt), 'dd.MM.yyyy')
          : '',
        <span
          className={`${
            isOverdue ? 'taxes__transportation__table__row-danger' : ''
          }`}
        >
          {displayPrice(
            user.amount,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            user.currency
          )}
        </span>,
        user.dueDate ? format(new Date(user.dueDate), 'dd.MM.yyyy') : '-',
        penaltyApplied
          ? displayPrice(
              user.penalty,
              t('PAYMENTS.LABELS.BGN'),
              t('PAYMENTS.LABELS.EUR'),
              user.currency
            )
          : '-',
        <span
          className={`${
            !user.isPaid && penaltyApplied
              ? 'taxes__transportation__table__row-danger'
              : ''
          }`}
        >
          {displayPrice(
            totalAmount,
            t('PAYMENTS.LABELS.BGN'),
            t('PAYMENTS.LABELS.EUR'),
            user.currency
          )}
        </span>,
        user.isPaid ? (
          <Tag type="success">{t('TAXES.LABELS.PAID')}</Tag>
        ) : (
          <Tag type="danger">{t('TAXES.LABELS.NOT_PAID')}</Tag>
        ),
        user.isPaid
          ? user.isPaidWithCard
            ? t('TAXES.LABELS.CARD')
            : t('TAXES.LABELS.TRANSFER')
          : '',
        !user.isPaid ? (
          <ActionMenuHorizontal
            options={[
              {
                label: t('ACTIONS.SEND_REMINDER'),
                hidden: true,
                onClick: () => {}, //TODO
              },
              {
                label: t('ACTIONS.MARK_AS_PAID'),
                onClick: () => onMarkAsPaid([user.id]),
              },
              {
                label: t('ACTIONS.DELETE'),
                onClick: () => onDelete([user.id]),
              },
            ]}
          />
        ) : (
          ''
        ),
      ];
      return [...acc, userArray];
    }, []);
  }, [adminSchoolTaxes?.result, t, onMarkAsPaid, control, onDelete]);

  return (
    <div className="taxes__school">
      <div className="taxes__school__content">
        {isSuccess && adminSchoolTaxes && (
          <>
            <ExportButton
              exportRequest={exportReport}
              successMessage={t('TAXES.MESSAGES.EXPORT_SUCCESS')}
            />
            <div className="taxes__school__filter">
              <TaxesFilters pathMatch={match} showPaymentPeriod={true} />
              {renderHeader(users, dirtyFields)}
            </div>

            {adminSchoolTaxes.totalCount > 0 ? (
              <>
                <Table rows={tableChildren} headers={headers} />
                <div className="users__pagination">
                  <Pagination data={adminSchoolTaxes} />
                </div>
              </>
            ) : (
              <EmptyState
                message={t('TAXES.MESSAGES.NO_TRANSPORTATION_TAXES')}
              />
            )}
          </>
        )}

        {isError && (
          <EmptyState message={t('TAXES.MESSAGES.NO_TRANSPORTATION_TAXES')} />
        )}
        {isPending && <TableSkeleton />}
      </div>
    </div>
  );
}
